import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, AMENITY_LABELS, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";
import { toDisplayUrl } from "@/components/PhotoUpload";
import { StarBadge, StarRow } from "@/components/Stars";

const DEFAULT_HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 8..20 fallback

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();

  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [date, setDate] = useState(new Date());
  const [bookedHours, setBookedHours] = useState([]);
  const [openHours, setOpenHours] = useState(DEFAULT_HOURS);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [agreedWaiver, setAgreedWaiver] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => nav("/browse"));
    api.get(`/listings/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [id, nav]);

  useEffect(() => {
    if (!listing) return;
    const dstr = format(date, "yyyy-MM-dd");
    api.get(`/listings/${listing.id}/availability`, { params: { date: dstr } })
      .then((r) => {
        setBookedHours(r.data.booked_hours || []);
        setOpenHours(r.data.open_hours && r.data.open_hours.length ? r.data.open_hours : []);
      });
    setStart(null); setEnd(null);
  }, [listing, date]);

  const isBooked = (h) => bookedHours.includes(h);

  const onHour = (h) => {
    if (isBooked(h)) return;
    if (start === null) { setStart(h); setEnd(h + 1); return; }
    if (h < start) { setStart(h); setEnd(h + 1); return; }
    // extend selection to h+1 if no booked hour in between
    const range = [];
    for (let i = start; i <= h; i++) range.push(i);
    if (range.some((r) => bookedHours.includes(r))) {
      setStart(h); setEnd(h + 1); return;
    }
    setEnd(h + 1);
  };

  const hoursSelected = start !== null && end !== null ? end - start : 0;
  const total = listing && hoursSelected ? (hoursSelected * listing.hourly_rate).toFixed(2) : "0.00";

  const submit = async () => {
    if (!user) { nav("/login"); return; }
    if (start === null) { toast.error("Pick a time"); return; }
    if (!agreedWaiver) { toast.error("Please accept the booking waiver"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        listing_id: listing.id,
        date: format(date, "yyyy-MM-dd"),
        start_hour: start,
        end_hour: end,
        agreed_waiver: true,
      });
      toast.success("Booking requested — the host will confirm shortly");
      nav("/trips");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!listing) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12 space-y-6">
        <Skeleton className="h-8 w-96"/>
        <Skeleton className="h-[420px] w-full"/>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="label-eyebrow mb-3">{listing.neighborhood}</div>
        <h1 className="font-display font-medium text-4xl sm:text-5xl tracking-tight max-w-3xl">{listing.title}</h1>
        <div className="mt-3 flex items-center gap-2 text-sm text-neutral-500">
          <MapPin size={14}/> {listing.address}
        </div>
      </div>

      {/* Bento image grid */}
      <div className="grid md:grid-cols-12 gap-3 mb-10">
        <div className="md:col-span-8 md:row-span-2 aspect-[16/10] overflow-hidden border border-neutral-200">
          <img src={toDisplayUrl(listing.photos?.[0])} alt="" className="w-full h-full object-cover"/>
        </div>
        <div className="md:col-span-4 aspect-[4/3] overflow-hidden border border-neutral-200">
          <img src={toDisplayUrl(listing.photos?.[1] || listing.photos?.[0])} alt="" className="w-full h-full object-cover"/>
        </div>
        <div className="md:col-span-4 aspect-[4/3] overflow-hidden border border-neutral-200">
          <img src={toDisplayUrl(listing.photos?.[2] || listing.photos?.[0])} alt="" className="w-full h-full object-cover"/>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          {/* Host */}
          <div className="flex items-center justify-between border-b border-neutral-200 pb-6">
            <Link
              to={`/hosts/${listing.host?.id}`}
              data-testid="host-link"
              className="flex items-center gap-4 group"
            >
              {listing.host?.profile_photo ? (
                <img src={listing.host.profile_photo} alt="" className="w-12 h-12 rounded-full object-cover"/>
              ) : (
                <div className="w-12 h-12 rounded-full bg-neutral-900 text-white flex items-center justify-center font-medium">
                  {listing.host?.name?.[0]}
                </div>
              )}
              <div>
                <div className="font-medium group-hover:underline">Hosted by {listing.host?.name}</div>
                <div className="flex items-center gap-2 text-xs text-neutral-500 flex-wrap">
                  {listing.host?.id_verified && <span className="inline-flex items-center gap-1"><ShieldCheck size={12}/> ID verified</span>}
                  {listing.has_insurance && (
                    <>
                      {listing.host?.id_verified && <span className="text-neutral-300">·</span>}
                      <span data-testid="insurance-badge" className="inline-flex items-center gap-1">
                        <ShieldCheck size={12}/> Insurance on file
                        {listing.insurance_expires ? ` · expires ${listing.insurance_expires.slice(0,7)}` : ""}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </Link>
          </div>

          <div>
            <div className="label-eyebrow mb-3">About this den</div>
            <p className="text-neutral-700 leading-relaxed max-w-2xl">{listing.description}</p>
          </div>

          <div>
            <div className="label-eyebrow mb-4">Amenities</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {listing.amenities?.map((a) => (
                <div key={a} className="border border-neutral-200 px-4 py-3 text-sm">
                  {AMENITY_LABELS[a] || a}
                </div>
              ))}
            </div>
          </div>

          {/* Reviews */}
          <div>
            <div className="flex items-baseline justify-between mb-4">
              <div className="label-eyebrow">Reviews</div>
              <StarBadge rating={listing.avg_rating} count={listing.review_count}/>
            </div>
            {reviews.length === 0 ? (
              <div className="border border-dashed border-neutral-300 p-8 text-center text-neutral-500 text-sm">
                No reviews yet. Be the first after your session.
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((r) => (
                  <div key={r.id} data-testid={`review-${r.id}`} className="border border-neutral-200 p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-neutral-900 text-white text-xs flex items-center justify-center">
                          {r.guest?.name?.[0] || "G"}
                        </div>
                        <div className="text-sm font-medium">{r.guest?.name || "Guest"}</div>
                      </div>
                      <StarRow value={r.stars} size={13} testIdPrefix={`review-${r.id}-star`}/>
                    </div>
                    {r.comment && <p className="text-neutral-700 text-sm leading-relaxed">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Booking panel */}
        <aside className="lg:col-span-5">
          <div className="border border-neutral-200 sticky top-24">
            <div className="p-6 border-b border-neutral-200 flex items-baseline justify-between">
              <div>
                <span className="font-display font-semibold text-3xl">${listing.hourly_rate}</span>
                <span className="text-neutral-500 text-sm ml-1">/ hour</span>
              </div>
              <div className="label-eyebrow">Book by the hour</div>
            </div>

            <div className="p-6 border-b border-neutral-200">
              <div className="label-eyebrow mb-3">Date</div>
              <Calendar
                data-testid="booking-calendar"
                mode="single"
                selected={date}
                onSelect={(d) => d && setDate(d)}
                disabled={{ before: new Date(new Date().setHours(0,0,0,0)) }}
                className="p-0"
              />
            </div>

            <div className="p-6 border-b border-neutral-200">
              <div className="label-eyebrow mb-3">Time · {format(date, "EEE, MMM d")}</div>
              {openHours.length === 0 ? (
                <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 p-6 text-center">
                  Host is not available on this day. Try another date.
                </div>
              ) : (
              <div className="grid grid-cols-4 gap-2">
                {openHours.map((h) => {
                  const booked = isBooked(h);
                  const inRange = start !== null && end !== null && h >= start && h < end;
                  return (
                    <button
                      key={h}
                      data-testid={`hour-${h}`}
                      disabled={booked}
                      onClick={() => onHour(h)}
                      className={`h-10 text-xs border transition-colors ${
                        booked
                          ? "border-neutral-200 bg-neutral-100 text-neutral-300 line-through cursor-not-allowed"
                          : inRange
                          ? "border-neutral-900 bg-neutral-900 text-white"
                          : "border-neutral-300 text-neutral-700 hover:border-neutral-900"
                      }`}
                    >
                      {String(h).padStart(2, "0")}:00
                    </button>
                  );
                })}
              </div>
              )}
              <div className="mt-3 text-xs text-neutral-500">
                Tap a start hour, then tap an end hour to extend. Greyed slots are booked.
              </div>
            </div>

            <div className="p-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">{hoursSelected} hour{hoursSelected !== 1 ? "s" : ""}</span>
                <span>${total}</span>
              </div>
              <div className="flex justify-between font-medium border-t border-neutral-200 pt-3">
                <span>Total</span>
                <span data-testid="booking-total" className="font-display text-lg">${total}</span>
              </div>

              {user && hoursSelected > 0 && (
                <label className="flex items-start gap-2 text-[12px] leading-relaxed cursor-pointer select-none border border-neutral-200 p-3 mt-2">
                  <Checkbox
                    data-testid="booking-waiver-checkbox"
                    checked={agreedWaiver}
                    onCheckedChange={(v) => setAgreedWaiver(!!v)}
                    className="mt-0.5 border-neutral-400 data-[state=checked]:bg-neutral-900 data-[state=checked]:border-neutral-900"
                  />
                  <span className="text-neutral-700">
                    I understand I am booking a private home workspace, not a commercial office. I accept full
                    responsibility for my own safety and any use of the Host's equipment, software, or AI tools
                    during my booking, and I release DenShare from liability for any injury, loss, or damage
                    that occurs during my visit, except where caused by DenShare's own gross negligence.
                  </span>
                </label>
              )}

              <Button
                data-testid="request-booking-btn"
                onClick={submit}
                disabled={submitting || hoursSelected === 0 || (!!user && !agreedWaiver)}
                className="w-full h-12 rounded-md bg-neutral-900 hover:bg-neutral-800 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {user ? (submitting ? "Requesting..." : <>Confirm booking request <ArrowRight size={16} className="ml-1"/></>) : "Sign in to book"}
              </Button>
              <div className="text-[11px] text-neutral-500 text-center">
                Hosts confirm requests within a few hours.
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
